import React, { useEffect, useMemo, useState } from 'react';
import { contentApi } from '../../services/api/content.api';
import type { CriteriaCatalogItem, GameMode, ImpactRule } from '@business-game/shared';

export const AdminImpactsPage: React.FC = () => {
  const [gameMode, setGameMode] = useState<GameMode>('teamlab');
  const [criterias, setCriterias] = useState<CriteriaCatalogItem[]>([]);
  const [selectedCriteriaId, setSelectedCriteriaId] = useState<number | null>(null);
  const [rules, setRules] = useState<ImpactRule[]>([]);
  const [saving, setSaving] = useState(false);
  const impactColumns = useMemo(
    () =>
      gameMode === 'teamlab'
        ? [
            { key: 'impact1' as const, label: 'Dimension 1' },
            { key: 'impact2' as const, label: 'Dimension 2' },
            { key: 'impact3' as const, label: 'Dimension 3' },
          ]
        : [
            { key: 'impact1' as const, label: 'Dimension 1' },
            { key: 'impact2' as const, label: 'Dimension 2' },
            { key: 'impact3' as const, label: 'Dimension 3' },
            { key: 'impact4' as const, label: 'Dimension 4' },
          ],
    [gameMode]
  );

  useEffect(() => {
    const load = async () => {
      const data = await contentApi.listCriteriaCatalog(gameMode);
      setCriterias(data);
      setSelectedCriteriaId((current) => current ?? data[0]?.id ?? null);
    };

    void load();
  }, [gameMode]);

  useEffect(() => {
    const loadRules = async () => {
      if (!selectedCriteriaId) {
        setRules([]);
        return;
      }

      const data = await contentApi.getImpactRules(selectedCriteriaId);
      setRules(data);
    };

    void loadRules();
  }, [selectedCriteriaId]);

  const criteriaOptions = useMemo(() => criterias.filter((criteria) => criteria.isActive), [criterias]);

  const updateRule = (round: number, key: 'impact1' | 'impact2' | 'impact3' | 'impact4', value: number) => {
    setRules((current) => current.map((rule) => (rule.round === round ? { ...rule, [key]: value } : rule)));
  };

  const handleSave = async () => {
    if (!selectedCriteriaId) {
      return;
    }

    setSaving(true);
    try {
      await contentApi.replaceImpactRules(selectedCriteriaId, {
        criteriaId: selectedCriteriaId,
        rules: rules.map((rule) => ({
          round: rule.round,
          impact1: rule.impact1,
          impact2: rule.impact2,
          impact3: rule.impact3,
          impact4: rule.impact4,
        })),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex min-h-full flex-col font-sans text-slate-900">
      <main className="flex-grow w-full px-4 py-8 sm:px-6 lg:px-8">
        <div className="mb-8 w-full rounded-[2rem] border border-violet-200 bg-gradient-to-br from-violet-50 via-white to-slate-50 p-6 shadow-sm">
          <div className="space-y-6">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-indigo-500">Impacts</div>
            <h1 className="mt-2 text-2xl font-black tracking-tight text-slate-900">Impacts / poids</h1>
            <p className="mt-2 text-sm text-slate-500">
              Modifiez les regles de score par critere et par periode de decroissance.
            </p>
          </div>

          <SurfaceCard className="p-5">
            <div className="mb-4">
              <div className="text-xs font-bold uppercase tracking-[0.25em] text-indigo-500">Configuration</div>
              <h2 className="mt-2 text-lg font-bold text-slate-900">Choisir le mode et le critere</h2>
              <p className="mt-1 text-sm text-slate-500">
                Selectionne d'abord un mode puis un critere pour modifier sa matrice d'impacts sur les 10 rounds.
              </p>
            </div>

            <div className="grid gap-4 lg:grid-cols-[220px_minmax(0,1fr)]">
              <FilterCard label="Mode">
                <select
                  value={gameMode}
                  onChange={(event) => setGameMode(event.target.value as GameMode)}
                  className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="teamlab">TeamLab</option>
                  <option value="leadlab">LeadLab</option>
                </select>
              </FilterCard>

              <FilterCard label="Critere">
                <select
                  value={selectedCriteriaId ?? ''}
                  onChange={(event) => setSelectedCriteriaId(Number(event.target.value))}
                  className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500"
                >
                  {criteriaOptions.map((criteria) => (
                    <option key={criteria.id} value={criteria.id}>
                      {criteria.code}
                    </option>
                  ))}
                </select>
              </FilterCard>
            </div>
          </SurfaceCard>

          <SurfaceCard className="p-5">
            <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.25em] text-indigo-500">Matrice</div>
                <h2 className="mt-2 text-lg font-bold text-slate-900">Table des impacts par round</h2>
                <p className="mt-1 text-sm text-slate-500">
                  {gameMode === 'teamlab'
                    ? 'TeamLab utilise 3 dimensions. Renseigne les poids pour chaque round.'
                    : 'LeadLab utilise 4 dimensions. Renseigne les poids pour chaque round.'}
                </p>
              </div>
              <div className="rounded-2xl border border-indigo-200 bg-indigo-50/70 px-4 py-3 text-sm font-medium text-indigo-700">
                {impactColumns.length} dimensions affichees
              </div>
            </div>
          </SurfaceCard>

          <SurfaceCard className="overflow-hidden p-0">
            <div className="pt-2">
              <table className="min-w-full divide-y divide-slate-200 text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-3 text-left font-bold text-slate-500">Round</th>
                    {impactColumns.map((column) => (
                      <th key={column.key} className="px-4 py-3 text-left font-bold text-slate-500">
                        {column.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {rules.map((rule) => (
                    <tr key={rule.round}>
                      <td className="px-4 py-3 font-semibold text-slate-700">{rule.round}</td>
                      {impactColumns.map((column) => (
                        <td key={column.key} className="px-4 py-3">
                          <input
                            type="number"
                            value={rule[column.key]}
                            onChange={(event) => updateRule(rule.round, column.key, Number(event.target.value))}
                            className="w-full rounded-xl border border-slate-200 px-3 py-2 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500"
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </SurfaceCard>

          <button
            type="button"
            onClick={() => void handleSave()}
            className="rounded-2xl bg-indigo-600 px-5 py-3 font-semibold text-white shadow-lg shadow-indigo-200 transition-all hover:-translate-y-0.5 hover:bg-indigo-700 active:scale-95 disabled:opacity-50"
            disabled={saving || !selectedCriteriaId}
          >
            Sauver les impacts
          </button>
          </div>
        </div>
      </main>
    </div>
  );
};

const FilterCard: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <label className="mb-2 block text-xs font-bold uppercase tracking-wider text-slate-500">{label}</label>
    {children}
  </div>
);

const SurfaceCard: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
  <div
    className={`rounded-2xl border border-slate-200 bg-white shadow-sm transition-all duration-300 hover:border-indigo-200 hover:shadow-lg ${className}`.trim()}
  >
    {children}
  </div>
);
